import { inject, Injectable, signal } from '@angular/core';
import { AlertService, IntegrationService, UserService } from '@app/core/services';
import { Checker } from '@app/shared/utils';
import { notNull } from '@app/shared/utils/operators';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Builder, ShortcutBuilder } from './shortcut';
import { PaisEnum } from '@app/core/models';

@Injectable({
  providedIn: 'root',
})
export class ShortcutTeacher implements ShortcutBuilder {
  private readonly alertService = inject(AlertService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly translateService = inject(TranslateService);
  private readonly integrationService = inject(IntegrationService);
  private readonly userService = inject(UserService);

  public accept(store: AppState): boolean {
    return Checker.isTeacher(store?.funcao_padrao);
  }

  public readonly items: Builder[] = [
    () => ({
      title: 'geral.7edu',
      icon: 'icon-7edu',
      style: 'hover:bg-base-zoom-600 bg-base-zoom-100 hover:border-base-zoom-600 hover:text-white text-base-zoom-600',
      iconStyle: 'w-12 h-6 -rotate-15',
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => activeUser?.secretaria_sistema_id === 3),
      ),
      load: signal(false),
      open: () => this.openAcademicUrl(),
    }),
    () => ({
      title: 'menu.gestor-avaliacoes',
      icon: 'icon-gestor-avaliacoes',
      style:
        'hover:bg-modules-assessments-manager-600 bg-modules-assessments-manager-100 hover:border-modules-assessments-manager-600 hover:text-white text-modules-assessments-manager-600',
      iconStyle: 'w-8 h-8',
      permission: [1, 4],
      url: '/eclass-prova',
    }),
    () => ({
      title: 'menu.jogos',
      icon: 'icon-dice',
      style:
        'hover:bg-modules-games-600 bg-modules-games-100 hover:border-modules-games-600 hover:text-white text-modules-games-600',
      iconStyle: 'w-8 h-8',
      permission: [1, 6],
      url: '/jogos',
    }),
    () => ({
      title: 'academico.apontamentos',
      icon: 'icon-menu-educational',
      style:
        'hover:bg-modules-academic-600 bg-modules-academic-100 hover:border-modules-academic-600 hover:text-white text-modules-academic-600',
      iconStyle: 'w-8 h-6 mb-1 ml-1',
      permission: [19, 33],
      url: '/academico/apontamentos-educacionais',
    }),
    () => ({
      title: 'servicos.redacao',
      icon: 'icon-essay',
      style:
        'hover:bg-base-service-chave2-600 bg-base-service-chave2-100 hover:border-base-service-chave2-600 hover:text-white text-base-service-chave2-600',
      iconStyle: 'w-8 h-8',
      load: signal(false),
      open: () => this.openRedacao(),
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => activeUser?.pais_id === PaisEnum.Brasil),
      ),
    }),
    () => ({
      title: 'atalhos.replay',
      icon: 'icon-agenda',
      style:
        'hover:bg-modules-learning-trails-600 bg-modules-learning-trails-100 hover:border-modules-learning-trails-600 hover:text-white text-modules-learning-trails-600',
      iconStyle: 'w-8 h-8',
      permission: [18, 32],
      url: '/trilhas-de-aprendizagem/cursos',
      params: { search: 'replay' },
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => activeUser?.pais_id === PaisEnum.Brasil),
      ),
    }),
    () => ({
      title: 'menu.paaeb',
      icon: 'icon-gestor-avaliacoes',
      style:
        'hover:bg-modules-corporate-university-600 bg-modules-corporate-university-100 hover:border-modules-corporate-university-600 hover:text-white text-modules-corporate-university-600',
      iconStyle: 'w-8 h-8',
      permission: [3, 73],
      url: '/prova-diagnostica',
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => !activeUser?.escola_externa),
      ),
    }),
  ];

  private openRedacao(): Observable<any> {
    return this.integrationService.estuda().pipe(
      catchError(() =>
        of({ status: false, msg: this.translateService.instant('servicos.erros.contato-administrador') }),
      ),
      switchMap((data) => {
        if (data.status) {
          const windows = window.open('', '_blank');
          if (!windows) {
            const message = this.translateService.instant('servicos.erros.permitir-popup');
            const img = `<br><img src="assets/images/pop-up-links.jpg" class="pt-3">`;
            return this.alertService.alert({ title: 'status.atencao', innerHtml: `${message} ${img}` });
          }
          windows.location.href = data.url;
          return of(true);
        }
        return this.alertService.alert({ title: 'status.atencao', innerHtml: data.msg });
      }),
    );
  }

  private openAcademicUrl(): Observable<any> {
    return this.store.select(AppSelectors.appFeature).pipe(
      filter((state) => !!state?.usuario_id),
      take(1),
      notNull(),
      switchMap((state) => this.userService.syncLoginIatec(state.usuario_id).pipe(catchError(() => of(null)))),
      switchMap(() => this.userService.loginIatec().pipe(catchError(() => of(null)))),
      tap((url) => window.open(url, '_blank')),
    );
  }
}
